import { useEffect, useState } from 'react';
import { RiCloseLine, RiDownloadLine } from '@remixicon/react';
import { useLang } from '../../i18n/translations';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'idanroh-install-prompt-dismissed-at';
const COOLDOWN_DAYS = 14;
const MIN_VISITS_BEFORE_SHOW = 2;
const VISITS_KEY = 'idanroh-visit-count';

export function InstallPrompt() {
  const { t } = useLang();
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const visits = parseInt(localStorage.getItem(VISITS_KEY) || '0', 10) + 1;
    localStorage.setItem(VISITS_KEY, String(visits));

    const lastDismiss = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    const cooldownExpired = Date.now() - lastDismiss > COOLDOWN_DAYS * 86400 * 1000;
    const shouldConsider = visits >= MIN_VISITS_BEFORE_SHOW && cooldownExpired;

    function onBeforeInstall(e: Event) {
      e.preventDefault();
      const evt = e as BeforeInstallPromptEvent;
      setEvent(evt);
      if (shouldConsider) setVisible(true);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  async function install() {
    if (!event) return;
    await event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === 'accepted') setVisible(false);
    else dismiss();
  }

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  }

  if (!visible || !event) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-32px)] max-w-[400px] z-40
        bg-white rounded-2xl border border-line-light shadow-[0_8px_32px_rgba(0,0,0,0.12)] p-4"
      style={{ animation: 'slideUp 0.3s ease-out' }}
      role="dialog"
      aria-label="Installer l'application"
    >
      <button
        onClick={dismiss}
        aria-label="Fermer"
        className="absolute top-3 end-3 w-7 h-7 rounded-full flex items-center justify-center text-ink-faint hover:bg-surface-ui"
      >
        <RiCloseLine className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pe-6">
        <div className="w-11 h-11 rounded-xl bg-brand flex items-center justify-center shrink-0">
          <RiDownloadLine className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-display font-semibold text-ink">
            {t.install.title}
          </p>
          <p className="text-[12px] font-display text-ink-muted mt-0.5 leading-snug">
            {t.install.body}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={install}
              className="flex-1 h-10 rounded-xl bg-brand text-white text-[13px] font-display font-semibold"
            >
              {t.install.install}
            </button>
            <button
              onClick={dismiss}
              className="h-10 px-3 rounded-xl text-[13px] font-display text-ink-muted border border-line-light"
            >
              {t.install.later}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translate(-50%, 16px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
